import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Label } from '@crestrewards/ui';
import { AlertCircle, ArrowLeft, MailCheck } from 'lucide-react';

import AuthShell from './AuthShell';
import { validateEmail } from './authValidation';
import * as authApi from './authApi';

/**
 * Step one of a password reset: ask for the email.
 *
 * **The confirmation is deliberately identical whether or not the account
 * exists.** The backend returns the same response either way so this endpoint
 * cannot be used to discover which addresses have accounts; showing "no such
 * user" here would reintroduce exactly the leak the backend went out of its way
 * to close. Do not add a not-found branch, however helpful it seems.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [formError, setFormError] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (pending) return;

    const error = validateEmail(email);
    setFieldError(error ?? '');
    setFormError('');
    if (error) return;

    setPending(true);
    try {
      await authApi.forgotPassword({ email: email.trim() });
      setSent(true);
    } catch (err) {
      // A rate-limit or network failure is worth showing. An "unknown account"
      // never reaches here, because the backend does not report one.
      setFormError(err.message);
    } finally {
      setPending(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If that address belongs to a CrestRewards account, a reset link is on its way."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border-hairline border-border bg-background px-3 py-2.5 text-meta text-muted-foreground">
            <MailCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              The link works once and expires in 30 minutes. If nothing arrives, check your
              spam folder before requesting another.
            </span>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We'll email you a link to choose a new one."
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {formError ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border-hairline border-destructive/30 bg-destructive-tint px-3 py-2.5 text-meta text-destructive-on-tint"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{formError}</span>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="you@crestrewards.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setFieldError('');
              setFormError('');
            }}
            disabled={pending}
            aria-invalid={Boolean(fieldError)}
            aria-describedby={fieldError ? 'email-error' : undefined}
          />
          {fieldError ? (
            <p id="email-error" className="text-label text-destructive-on-tint">
              {fieldError}
            </p>
          ) : null}
        </div>

        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Sending…' : 'Send reset link'}
        </Button>

        <Link
          to="/login"
          className="flex items-center justify-center gap-1.5 text-label text-muted-foreground underline-offset-4 hover:text-primary-hover hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to sign in
        </Link>
      </form>
    </AuthShell>
  );
}
